import { LockOutlined } from "@mui/icons-material";
import { Box, InputAdornment } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { PasswordVisibilityToggle } from "../../../../../components/PasswordVisibilityToggle/PasswordVisibilityToggle";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { CLIPBOARD_STEP_COLUMNS_SX, CLIPBOARD_STEP_FORM_COLUMN_SX } from "./clipboardWizardLayout";

type Props = {
	email: string;
	onEmailChange: (value: string) => void;
	password: string;
	onPasswordChange: (value: string) => void;
	/** Already-translated failure sentence from the last attempt, or null. */
	errorMessage: string | null;
	/** Enter in either field runs the step's primary action. */
	onSubmit: () => void;
	/** The "three switches" status list — rendered as the step's aside so the model stays on screen. */
	aside: React.ReactNode;
};

/**
 * Step 1 — validate the Clipboard login and (in the same call) list the workplaces it manages.
 *
 * Nothing is written here, and the banner says so: enrolling a department before a facility is chosen
 * would go live on the next 15-minute sync tick with no facility mapping and no role catalog.
 *
 * The password field is `SystemValidationTextField` (the house TextField wrapper) driving a native
 * `type="password"` plus the existing `PasswordVisibilityToggle` — the toolkit has no password input and
 * one is not worth inventing for a single support-only field.
 */
export const ClipboardCredentialsStep: FC<Props> = ({ email, onEmailChange, password, onPasswordChange, errorMessage, onSubmit, aside }) => {
	const { t } = useTranslation();
	const [showPassword, setShowPassword] = useState(false);

	const submitOnEnter = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") onSubmit();
	};

	return (
		<Box sx={CLIPBOARD_STEP_COLUMNS_SX}>
			<Box sx={CLIPBOARD_STEP_FORM_COLUMN_SX}>
				<SystemBanner
					tone="info"
					icon={<LockOutlined />}
					title={t("department.config.clipboard.wizard.credentials.safe.title")}
					description={t("department.config.clipboard.wizard.credentials.safe.description")}
					dataTestId="clipboard-wizard-credentials-safe"
				/>
				<SystemValidationTextField
					label={t("department.config.clipboard.wizard.credentials.email")}
					value={email}
					onChange={(event) => onEmailChange(event.target.value)}
					onKeyDown={submitOnEnter}
					inputType="email"
					fullWidth
					autoFocus
					reserveHeightForHelperText={false}
					injectedProps={{ slotProps: { htmlInput: { "data-testid": "clipboard-wizard-email", autoComplete: "off" } } }}
					dataTestId="clipboard-wizard-email-field"
				/>
				<SystemValidationTextField
					label={t("department.config.clipboard.wizard.credentials.password")}
					value={password}
					onChange={(event) => onPasswordChange(event.target.value)}
					onKeyDown={submitOnEnter}
					inputType={showPassword ? "text" : "password"}
					fullWidth
					reserveHeightForHelperText={false}
					inputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<PasswordVisibilityToggle showPassword={showPassword} onToggle={() => setShowPassword((prev) => !prev)} />
							</InputAdornment>
						),
					}}
					injectedProps={{ slotProps: { htmlInput: { "data-testid": "clipboard-wizard-password", autoComplete: "new-password" } } }}
					dataTestId="clipboard-wizard-password-field"
				/>
				{errorMessage && (
					<InlineHint tone="warning" dataTestId="clipboard-wizard-credentials-error">
						{errorMessage}
					</InlineHint>
				)}
			</Box>
			{aside}
		</Box>
	);
};
